import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PublicHeader from "@/components/PublicHeader";
import { api, errMsg, homeFor } from "@/lib/api";
import { getAttribution } from "@/lib/attribution";

const PERSONAL = /@(gmail|googlemail|yahoo|ymail|hotmail|outlook|live|msn|aol|icloud|me|mac|proton|protonmail|pm|gmx|mail|yandex|zohomail|qq|163|126|fastmail|hey|comcast|verizon|att|sbcglobal)\.(com|ca|net|me|co\.uk)$/i;
const LI_ERRORS = {
  recruiter_password_only: "Hiring companies sign in with their company email and password.",
  expired: "That LinkedIn sign-in link expired. Please try again.",
  denied: "LinkedIn sign-in was cancelled.",
  email: "LinkedIn didn't share a verified email address. Please verify your email on LinkedIn or use another sign-in method.",
  disabled: "This account is disabled.",
};
import { useAuth } from "@/context/AuthContext";

const Shell = ({ title, sub, children }) => (
  <div className="min-h-screen"><PublicHeader />
    <main className="mx-auto max-w-md px-4 py-16">
      <h1 className="text-3xl font-extrabold" data-testid="auth-title">{title}</h1>
      {sub && <p className="mt-2 text-sm text-slate-600">{sub}</p>}
      <div className="mt-8 rounded-2xl border bg-white p-7 shadow-sm">{children}</div>
    </main>
  </div>
);

const Field = ({ id, labelText, ...p }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{labelText}</Label><Input id={id} data-testid={`${id}-input`} {...p} /></div>
);

function SocialButtons() {
  const [ownGoogle, setOwnGoogle] = useState(null);
  useEffect(() => { api.get("/auth/google/status").then((r) => setOwnGoogle(r.data.own_client)).catch(() => setOwnGoogle(false)); }, []);
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (ownGoogle) {
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/start?origin=${encodeURIComponent(window.location.origin)}`;
      return;
    }
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="space-y-2">
      <Button type="button" variant="outline" className="w-full" onClick={google} data-testid="google-login-button">Continue with Google</Button>
      <Button type="button" variant="outline" className="w-full" onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/linkedin/start?origin=${encodeURIComponent(window.location.origin)}`; }} data-testid="linkedin-login-button"><Linkedin className="mr-2 h-4 w-4" />Continue with LinkedIn</Button>
      <div className="relative py-3 text-center text-xs text-slate-400"><span className="bg-white px-2">or with email</span></div>
    </div>
  );
}

export function Login() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const liErr = params.get("linkedin_error");
  const gErr = params.get("google_error");
  useEffect(() => {
    if (liErr) { toast.error(LI_ERRORS[liErr] || "LinkedIn sign-in failed. Please try again."); }
    if (gErr) { toast.error("Google sign-in didn't complete. Please try again in a moment.", { id: "google-error" }); }
  }, [liErr, gErr]);
  if (user) return <Navigate to={homeFor(user)} replace />;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/login", f); setUser({ ...data, role_confirmed: true }); nav(homeFor(data)); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Sign in" sub="Welcome back to your career operating system.">
      <SocialButtons />
      <form onSubmit={submit} className="space-y-4">
        <Field id="login-email" labelText="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field id="login-password" labelText="Password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <Button className="w-full" disabled={busy} data-testid="login-submit-button">{busy ? "Signing in…" : "Sign in"}</Button>
      </form>
      <div className="mt-5 flex justify-between text-sm"><Link to="/forgot-password" className="text-primary" data-testid="forgot-password-link">Forgot password?</Link><Link to="/register" className="text-primary" data-testid="go-register-link">Create account</Link></div>
    </Shell>
  );
}

export function Register() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", password: "", role: params.get("role") === "recruiter" ? "recruiter" : "candidate", org_name: "" });
  const [busy, setBusy] = useState(false);
  if (user) { const next = params.get("next"); return <Navigate to={user.role === "recruiter" && (!user.email_verified || user.personal_email) ? "/recruiter/verify" : user.role === "candidate" && next && /^\/app\/[a-z]+$/.test(next) ? next : homeFor(user)} replace />; }
  const submit = async (e) => {
    e.preventDefault();
    if (f.role === "recruiter" && PERSONAL.test(f.email)) { toast.error("Please use your company or agency email. Personal emails like Gmail or Yahoo aren't accepted for hiring accounts."); return; }
    setBusy(true);
    try { const { data } = await api.post("/auth/register", { ...f, attribution: getAttribution() }); setUser({ ...data, role_confirmed: true }); const next = params.get("next"); nav(data.role === "recruiter" ? "/recruiter/verify" : next && /^\/app\/[a-z]+$/.test(next) ? next : homeFor(data)); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Create your account" sub="Free to start. No card required.">
      <div className="mb-5 grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-1">
        {["candidate", "recruiter"].map((r) => (
          <button key={r} type="button" data-testid={`register-role-${r}`} onClick={() => setF({ ...f, role: r })} className={`rounded-lg py-2 text-sm font-semibold capitalize ${f.role === r ? "bg-white shadow-sm" : "text-slate-500"}`}>{r === "candidate" ? "I'm a candidate" : "I'm hiring"}</button>
        ))}
      </div>
      {f.role === "candidate" && <SocialButtons />}
      <form onSubmit={submit} className="space-y-4">
        <Field id="register-name" labelText="Full name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        {f.role === "recruiter" && <Field id="register-org" labelText="Company / agency" required value={f.org_name} onChange={(e) => setF({ ...f, org_name: e.target.value })} />}
        <Field id="register-email" labelText={f.role === "recruiter" ? "Company or agency email" : "Email"} type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        {f.role === "recruiter" && <p className="-mt-2 text-xs text-muted-foreground" data-testid="register-work-email-hint">For security, Gmail, Yahoo, Outlook and other personal emails aren't accepted. We'll email you a 6-digit code to verify it.</p>}
        <Field id="register-password" labelText="Password (min 8 characters)" type="password" minLength={8} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <Button className="w-full" disabled={busy} data-testid="register-submit-button">{busy ? "Creating…" : "Create account"}</Button>
      </form>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => { e.preventDefault(); await api.post("/auth/forgot-password", { email }).catch(() => {}); setDone(true); };
  return (
    <Shell title="Reset password">
      {done ? <p className="text-sm" data-testid="forgot-done-message">If an account exists for that email, a reset link is on its way. It expires in 1 hour — check your spam folder if you don't see it.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <Field id="forgot-email" labelText="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button className="w-full" data-testid="forgot-submit-button">Send reset link</Button>
        </form>
      )}
    </Shell>
  );
}

export function Reset() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: params.get("token"), password: pw }); toast.success("Password updated"); nav("/login"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        <Field id="reset-password" labelText="New password" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} />
        <Button className="w-full" data-testid="reset-submit-button">Update password</Button>
      </form>
    </Shell>
  );
}
